'use strict';
import React from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Typography,
	Box,
	IconButton,
	CircularProgress
} from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import * as config from '@/config/env.config';
import { Brand } from '@/lenses/uilenses/theme/colors';
import { navigateToIBP } from '@/utils/auth0Token';

// Component for individual project item
const ProjectItem = ({ project, onClose }) => {
	const displayTitle = project.customerProjectTitle || 
		(project.projectTitle ? project.projectTitle.replace(project.projectCode, '') : '');

	const href = config.API_BIOVIZ + `?projectId=${project.projectId}`;
	
	const handleClick = (e) => {
		e.preventDefault();
		onClose();
		navigateToIBP(href);
	};

	return (
		<ListItem disablePadding>
			<ListItemButton
				onClick={handleClick}
				sx={{
					borderRadius: 2,
					mb: 1,
					'&:hover': {
						background: Brand.metaboliteWhite,
					}
				}}
			>
				<ListItemText
					primary={
						<Typography variant="subtitle1" sx={{ fontWeight: 600, color: Brand.darkModeBlue }}>
							{project.projectCode}
						</Typography>
					}
					secondary={
						<Typography variant="body2" sx={{ color: Brand.molecularBlue }}>
							{displayTitle}
						</Typography>
					}
				/>
			</ListItemButton>
		</ListItem>
	);
};

export const IBPProjectsModal = ({ open, onClose, projects = [], isLoading = false }) => {
	const handleOpenIBP = (e) => {
		if (e) e.preventDefault();
		onClose();
		navigateToIBP(config.API_BIOVIZ);
	};

	return (
		<Dialog 
			open={open} 
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 3,
					boxShadow: '0 8px 32px rgba(0, 29, 56, 0.2)',
				}
			}}
		>
			<DialogTitle sx={{ 
				background: `linear-gradient(135deg, ${Brand.activatedRed} 0%, ${Brand.molecularBlue} 100%)`,
				color: Brand.pureWhite,
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				padding: '20px 24px',
				fontWeight: 700
			}}>
				Integrated Bioinformatics Platform
				<IconButton
					onClick={onClose}
					sx={{ color: Brand.pureWhite }}
					size="small"
				>
					<Close />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ padding: '24px' }}>
				{isLoading ? (
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
						<CircularProgress sx={{ color: Brand.activatedRed, mb: 2 }} />
						<Typography variant="body1" sx={{ color: Brand.molecularBlue }}>
							Loading projects...
						</Typography>
					</Box>
				) : (
					<>
						<Typography variant="body2" sx={{ color: Brand.molecularBlue, mb: 3 }}>
							Select a project to access IBP, or open IBP directly to explore all available data.
						</Typography>
						
						{projects.length > 0 ? (
							<List sx={{ padding: 0 }}>
								{projects.map((project) => (
									<ProjectItem key={project.projectId} project={project} onClose={onClose} />
								))}
							</List>
						) : (
							<Box sx={{ textAlign: 'center', py: 4 }}>
								<Typography variant="body1" sx={{ color: Brand.molecularBlue, mb: 2 }}>
									No IBP projects available at this time.
								</Typography>
								<Button
									variant="contained"
									onClick={handleOpenIBP}
									sx={{
										background: Brand.activatedRed,
										'&:hover': {
											background: Brand.insightfulOrange,
										}
									}}
								>
									Open IBP <OpenInNew sx={{ ml: 1, fontSize: 18 }} />
								</Button>
							</Box>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions sx={{ padding: '16px 24px', borderTop: `1px solid ${Brand.precisionGrey}` }}>
				<Button onClick={onClose} sx={{ color: Brand.molecularBlue }}>
					Cancel
				</Button>
				{!isLoading && projects.length > 0 && (
					<Button
						onClick={handleOpenIBP}
						variant="contained"
						sx={{
							background: Brand.activatedRed,
							'&:hover': {
								background: Brand.insightfulOrange,
							}
						}}
					>
						Open IBP Directly <OpenInNew sx={{ ml: 1, fontSize: 18 }} />
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default IBPProjectsModal;

